// src/utils/userCache.js

/**
 * User Data Cache Utility
 * 
 * Implements a sessionStorage-based caching strategy for user data, similar to
 * patterns used by established auth solutions (Auth0, Firebase).
 * 
 * Features:
 * - Persists user data to sessionStorage (clears on browser close)
 * - Includes timestamp for cache invalidation
 * - Configurable cache duration (default: 1 hour)
 * - Secure: automatically clears on logout
 * 
 * This approach:
 * - Eliminates API calls on page navigation within a session
 * - Shows user data instantly from cache
 * - Maintains session security (sessionStorage clears on browser close)
 * - Handles stale cache gracefully with background refresh
 */

const USER_CACHE_KEY = 'trac_user_cache';
const UNITS_CACHE_KEY = 'trac_units_cache';
const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour in milliseconds

/**
 * Cache structure:
 * {
 *   data: <userData object>,
 *   timestamp: <ISO timestamp when cached>,
 *   version: <cache version for future migrations>
 * }
 */

// ===== User Data Cache =====

/**
 * Save user data to sessionStorage with timestamp
 * @param {Object} userData - The user data object to cache
 */
export const cacheUserData = (userData) => {
    if (!userData) return;
    
    try {
        const cacheEntry = {
            data: userData,
            timestamp: new Date().toISOString(),
            version: 1,
        };
        sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(cacheEntry));
    } catch (error) {
        // Silently fail if sessionStorage is not available (e.g., private browsing)
        console.warn('Failed to cache user data:', error);
    }
};

/**
 * Retrieve cached user data from sessionStorage
 * @returns {Object|null} The cached user data or null if not found/expired
 */
export const getCachedUserData = () => {
    try {
        const cached = sessionStorage.getItem(USER_CACHE_KEY);
        if (!cached) return null;
        
        const cacheEntry = JSON.parse(cached);
        return cacheEntry.data || null;
    } catch (error) {
        console.warn('Failed to read cached user data:', error);
        return null;
    }
};

/**
 * Check if the cached user data is still fresh (not stale)
 * @returns {boolean} True if cache is fresh, false if stale or missing
 */
export const isUserCacheFresh = () => {
    try {
        const cached = sessionStorage.getItem(USER_CACHE_KEY);
        if (!cached) return false;
        
        const cacheEntry = JSON.parse(cached);
        const cachedTime = new Date(cacheEntry.timestamp).getTime();
        const now = Date.now();
        
        return (now - cachedTime) < CACHE_DURATION_MS;
    } catch (error) {
        return false;
    }
};

/**
 * Clear user data from cache (call on logout)
 */
export const clearUserCache = () => {
    try {
        sessionStorage.removeItem(USER_CACHE_KEY);
    } catch (error) {
        console.warn('Failed to clear user cache:', error);
    }
};

// ===== Units Data Cache =====

/**
 * Save units data to sessionStorage with timestamp
 * @param {Array} units - The units array to cache
 * @param {string|null} unitId - The selected unit ID
 * @param {Array} selectedUnitIds - The selected unit IDs
 */
export const cacheUnitsData = (units, unitId, selectedUnitIds) => {
    if (!units || !Array.isArray(units)) return;
    
    try {
        const cacheEntry = {
            data: {
                units,
                unitId,
                selectedUnitIds,
            },
            timestamp: new Date().toISOString(),
            version: 1,
        };
        sessionStorage.setItem(UNITS_CACHE_KEY, JSON.stringify(cacheEntry));
    } catch (error) {
        console.warn('Failed to cache units data:', error);
    }
};

/**
 * Retrieve cached units data from sessionStorage
 * @returns {Object|null} The cached units data { units, unitId, selectedUnitIds } or null
 */
export const getCachedUnitsData = () => {
    try {
        const cached = sessionStorage.getItem(UNITS_CACHE_KEY);
        if (!cached) return null;
        
        const cacheEntry = JSON.parse(cached);
        return cacheEntry.data || null;
    } catch (error) {
        console.warn('Failed to read cached units data:', error);
        return null;
    }
};

/**
 * Check if the cached units data is still fresh
 * @returns {boolean} True if cache is fresh, false if stale or missing
 */
export const isUnitsCacheFresh = () => {
    try {
        const cached = sessionStorage.getItem(UNITS_CACHE_KEY);
        if (!cached) return false;
        
        const cacheEntry = JSON.parse(cached);
        const cachedTime = new Date(cacheEntry.timestamp).getTime();
        const now = Date.now();
        
        return (now - cachedTime) < CACHE_DURATION_MS;
    } catch (error) {
        return false;
    }
};

/**
 * Clear units data from cache (call on logout)
 */
export const clearUnitsCache = () => {
    try {
        sessionStorage.removeItem(UNITS_CACHE_KEY);
    } catch (error) {
        console.warn('Failed to clear units cache:', error);
    }
};

// ===== Combined Operations =====

/**
 * Clear all cached data (call on logout)
 */
export const clearAllCache = () => {
    clearUserCache();
    clearUnitsCache();
};
